import React, { useRef } from 'react';
import PropTypes from 'prop-types';
import styled, { css } from 'styled-components';
import { Swiper, SwiperSlide } from 'swiper/react';
import { Navigation, Mousewheel, Keyboard } from 'swiper/modules';
import media from 'styled-media-query';
import { breakpointsObject as bp } from '@firsttable/web-theme';
import { switchProp } from 'styled-tools';
import Review from 'next-web/components/organisms/Review';
import { Box, Flex, Col, Container, Row } from '../../../atoms';
import IconArrowLeftNav from '../../../atoms/Icon/IconArrowLeftNav';
import IconArrowRightNav from '../../../atoms/Icon/IconArrowRightNav';

const Arrow = styled(Box)`
  outline: 0;
`;

const mobileSpace = 20;
const space = 50;

const SwiperWrapper = styled(Box)`
  .swiper-wrapper {
    display: flex;
    box-sizing: border-box;
    padding: 8px ${mobileSpace}px 0;
    ${switchProp('count', {
      1: css`
        ${media.greaterThan(bp.m)`
        overflow: visible;
        padding-left: 20px !important;
        `};
      `,
      2: css`
        ${media.greaterThan(bp.m)`
        overflow: visible;
        padding-left: 20px !important;
        `};
      `,
    })};
  }
  ${media.greaterThan(bp.s)`
    .swiper-wrapper {
      padding: 0 ${space}px 90px;
      margin-bottom: -90px;
    }
    .swiper-slide {
      width: 590px;
    }
  `};
`;

const swiperComponentDefaultReviews = [];
const SwiperComponent = ({ reviews = swiperComponentDefaultReviews }) => {
  const swiperRef = useRef(null);

  const goNext = () => {
    if (swiperRef.current) {
      swiperRef.current.slideTo(swiperRef.current.activeIndex + 1);
    }
  };
  const goPrev = () => {
    if (swiperRef.current) {
      swiperRef.current.slideTo(swiperRef.current.activeIndex - 1);
    }
  };

  // use NoSSR as this causes issue on boot up of app and user scrolls
  return (
    <>
      <Container>
        <Row>
          <Col>
            <Box
              textAlign={['center', null, 'left']}
              display={[null, null, 'flex']}
              alignItems="center"
              justifyContent="flex-end"
              mb={['l', null, 'xxl']}
              mt={[0, 0, -60]}
            >
              <Flex
                mt={[25, 35, 15]}
                justifyContent="center"
                display={[
                  reviews.length > 1 ? 'flex' : 'none',
                  reviews.length > 1 ? 'flex' : 'none',
                  reviews.length > 2 ? 'flex' : 'none',
                ]}
              >
                <Arrow onClick={goPrev} role="button" aria-label="previous">
                  <IconArrowLeftNav />
                </Arrow>
                <Arrow ml="m" onClick={goNext} role="button" aria-label="next">
                  <IconArrowRightNav />
                </Arrow>
              </Flex>
            </Box>
          </Col>
        </Row>
      </Container>
      <SwiperWrapper width="100%" maxWidth="100%" count={reviews.length}>
        <Swiper
          modules={[Navigation, Mousewheel, Keyboard]}
          mousewheel={{
            forceToAxis: true,
            sensitivity: 1,
            releaseOnEdges: true,
            thresholdDelta: 2, // add a threshold to prevent double swipe bug: https://github.com/nolimits4web/swiper/issues/6942
          }}
          keyboard={{
            enabled: true,
            onlyInViewport: true,
            pageUpDown: true,
          }}
          grabCursor
          preventInteractionOnTransition
          slidesPerView="auto"
          spaceBetween={space}
          cssMode
          shouldSwiperUpdate
          onSwiper={(swiper) => {
            swiperRef.current = swiper;
          }}
        >
          {reviews.map(({ node }) => (
            <SwiperSlide key={node.id}>
              <Review
                card
                review={node}
                showRatings={false}
                showReadMore
                readMoreTextCount={220}
              />
            </SwiperSlide>
          ))}
        </Swiper>
      </SwiperWrapper>
    </>
  );
};

SwiperComponent.propTypes = {
  reviews: PropTypes.array,
};

export default SwiperComponent;
